import { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import CrudTable, { CrudField } from "@/components/admin/CrudTable";
import ZonesManager from "@/components/admin/ZonesManager";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useV2List, useV2Delete } from "@/hooks/useTransfersV2";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const airportFields: CrudField[] = [
  { key: "code", label: "IATA Code", required: true, placeholder: "PUJ" },
  { key: "name", label: "Name", required: true },
  { key: "city", label: "City" },
  { key: "country", label: "Country" },
  { key: "latitude", label: "Latitude", type: "number", step: "0.0000001", required: true },
  { key: "longitude", label: "Longitude", type: "number", step: "0.0000001", required: true },
  { key: "image_url", label: "Image URL" },
  { key: "sort_order", label: "Sort Order", type: "number" },
  { key: "is_active", label: "Active", type: "boolean" },
];

const zoneFields: CrudField[] = [
  { key: "name", label: "Name", required: true },
  { key: "slug", label: "Slug", required: true },
  { key: "description", label: "Description", type: "textarea" },
  { key: "base_price", label: "Base Price ($)", type: "number", step: "0.01" },
  { key: "price_multiplier", label: "Price Multiplier", type: "number", step: "0.01" },
  { key: "center_latitude", label: "Center Latitude", type: "number", step: "0.0000001" },
  { key: "center_longitude", label: "Center Longitude", type: "number", step: "0.0000001" },
  { key: "radius_km", label: "Radius (km)", type: "number", step: "0.01" },
  { key: "sort_order", label: "Sort Order", type: "number" },
  { key: "is_active", label: "Active", type: "boolean" },
];

const hotelFields = (zones: any[]): CrudField[] => [
  { key: "name", label: "Name", required: true },
  { key: "slug", label: "Slug" },
  { key: "zone_id", label: "Zone", type: "select", options: zones.map((z) => ({ value: z.id, label: z.name })) },
  { key: "address", label: "Address" },
  { key: "latitude", label: "Latitude", type: "number", step: "0.0000001", required: true },
  { key: "longitude", label: "Longitude", type: "number", step: "0.0000001", required: true },
  { key: "category", label: "Category", placeholder: "Resort / Hotel / Villa" },
  { key: "image_url", label: "Image URL" },
  { key: "sort_order", label: "Sort Order", type: "number" },
  { key: "is_active", label: "Active", type: "boolean" },
];

const serviceTypeFields: CrudField[] = [
  { key: "name", label: "Name", required: true },
  { key: "slug", label: "Slug", required: true },
  { key: "description", label: "Description", type: "textarea" },
  { key: "max_passengers", label: "Max Passengers", type: "number" },
  { key: "max_luggage", label: "Max Luggage", type: "number" },
  { key: "icon", label: "Icon", placeholder: "car / crown / sparkles / bus" },
  { key: "sort_order", label: "Sort Order", type: "number" },
  { key: "is_active", label: "Active", type: "boolean" },
];

const kmRateFields = (svcTypes: any[], zones: any[]): CrudField[] => [
  { key: "service_type_id", label: "Service Type", type: "select", required: true, options: svcTypes.map((s) => ({ value: s.id, label: s.name })) },
  { key: "zone_id", label: "Zone (optional)", type: "select", options: zones.map((z) => ({ value: z.id, label: z.name })) },
  { key: "base_fee", label: "Base Fee ($)", type: "number", step: "0.01" },
  { key: "price_per_km", label: "Price per km ($)", type: "number", step: "0.01" },
  { key: "minimum_price", label: "Minimum Price ($)", type: "number", step: "0.01" },
  { key: "free_km", label: "Free km", type: "number", step: "0.01" },
  { key: "is_active", label: "Active", type: "boolean" },
];

const vehicleFields = (svcTypes: any[]): CrudField[] => [
  { key: "service_type_id", label: "Service Type", type: "select", required: true, options: svcTypes.map((s) => ({ value: s.id, label: s.name })) },
  { key: "name", label: "Name", required: true },
  { key: "slug", label: "Slug" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "image_url", label: "Image URL" },
  { key: "passenger_capacity", label: "Passenger Capacity", type: "number" },
  { key: "luggage_capacity", label: "Luggage Capacity", type: "number" },
  { key: "features", label: "Features (JSON array)", type: "json" },
  { key: "sort_order", label: "Sort Order", type: "number" },
  { key: "is_featured", label: "Featured", type: "boolean" },
  { key: "is_active", label: "Active", type: "boolean" },
];

const promoFields: CrudField[] = [
  { key: "code", label: "Code", required: true, placeholder: "SUMMER20" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "discount_type", label: "Discount Type", type: "select", options: [{ value: "percentage", label: "Percentage" }, { value: "fixed", label: "Fixed Amount" }] },
  { key: "discount_value", label: "Discount Value", type: "number", step: "0.01" },
  { key: "max_uses", label: "Max Uses", type: "number" },
  { key: "valid_from", label: "Valid From (ISO)" },
  { key: "valid_until", label: "Valid Until (ISO)" },
  { key: "min_booking_amount", label: "Min Booking $", type: "number", step: "0.01" },
  { key: "is_active", label: "Active", type: "boolean" },
];

export default function AdminTransfersV2() {
  const { data: svcTypes = [] } = useV2List("transfer_service_types", true);
  const { data: zones = [] } = useV2List("transfer_zones", true);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Transfer Management</h1>
          <p className="text-muted-foreground">Manage airports, zones, hotels, vehicles, service types, rates, and promo codes</p>
        </div>

        <Tabs defaultValue="airports" className="w-full">
          <TabsList className="grid grid-cols-4 md:grid-cols-8 h-auto">
            <TabsTrigger value="airports">Airports</TabsTrigger>
            <TabsTrigger value="zones">Zones</TabsTrigger>
            <TabsTrigger value="hotels">Hotels</TabsTrigger>
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="rates">Km Rates</TabsTrigger>
            <TabsTrigger value="vehicles">Vehicles</TabsTrigger>
            <TabsTrigger value="promos">Promo Codes</TabsTrigger>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
          </TabsList>

          <TabsContent value="airports" className="mt-6">
            <CrudTable
              table="transfer_airports_v2"
              title="Airports"
              fields={airportFields}
              displayColumns={["code", "name", "city", "is_active"]}
              renderCell={(r, c) => c === "is_active" ? <Badge variant={r.is_active ? "default" : "secondary"}>{r.is_active ? "Active" : "Inactive"}</Badge> : String(r[c] ?? "-")}
            />
          </TabsContent>

          <TabsContent value="zones" className="mt-6">
            <ZonesManager />
          </TabsContent>


          <TabsContent value="hotels" className="mt-6">
            <CrudTable
              table="transfer_hotels_v2"
              title="Hotels & Points of Interest"
              fields={hotelFields(zones)}
              displayColumns={["name", "category", "zone_id", "is_active"]}
              renderCell={(r, c) => {
                if (c === "is_active") return <Badge variant={r.is_active ? "default" : "secondary"}>{r.is_active ? "Active" : "Inactive"}</Badge>;
                if (c === "zone_id") return zones.find((z: any) => z.id === r.zone_id)?.name ?? "-";
                return String(r[c] ?? "-");
              }}
            />
          </TabsContent>

          <TabsContent value="services" className="mt-6">
            <CrudTable
              table="transfer_service_types"
              title="Service Types"
              fields={serviceTypeFields}
              displayColumns={["name", "slug", "max_passengers", "is_active"]}
              renderCell={(r, c) => c === "is_active" ? <Badge variant={r.is_active ? "default" : "secondary"}>{r.is_active ? "Active" : "Inactive"}</Badge> : String(r[c] ?? "-")}
            />
          </TabsContent>

          <TabsContent value="rates" className="mt-6">
            <CrudTable
              table="transfer_km_rates"
              title="Km Rates"
              fields={kmRateFields(svcTypes, zones)}
              displayColumns={["service_type_id", "zone_id", "base_fee", "price_per_km", "minimum_price"]}
              renderCell={(r, c) => {
                if (c === "service_type_id") return svcTypes.find((s: any) => s.id === r.service_type_id)?.name ?? "-";
                if (c === "zone_id") return zones.find((z: any) => z.id === r.zone_id)?.name ?? "All zones";
                if (["base_fee", "price_per_km", "minimum_price"].includes(c)) return `$${r[c]}`;
                return String(r[c] ?? "-");
              }}
            />
          </TabsContent>

          <TabsContent value="vehicles" className="mt-6">
            <CrudTable
              table="transfer_vehicles_v2"
              title="Vehicles"
              fields={vehicleFields(svcTypes)}
              displayColumns={["name", "service_type_id", "passenger_capacity", "is_featured", "is_active"]}
              renderCell={(r, c) => {
                if (c === "service_type_id") return svcTypes.find((s: any) => s.id === r.service_type_id)?.name ?? "-";
                if (c === "is_active" || c === "is_featured") return <Badge variant={r[c] ? "default" : "secondary"}>{r[c] ? "Yes" : "No"}</Badge>;
                return String(r[c] ?? "-");
              }}
            />
          </TabsContent>

          <TabsContent value="promos" className="mt-6">
            <CrudTable
              table="transfer_promo_codes"
              title="Promo Codes"
              fields={promoFields}
              displayColumns={["code", "discount_type", "discount_value", "used_count", "is_active"]}
              renderCell={(r, c) => c === "is_active" ? <Badge variant={r.is_active ? "default" : "secondary"}>{r.is_active ? "Active" : "Inactive"}</Badge> : String(r[c] ?? "-")}
            />
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <BookingsList />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}

function BookingsList() {
  const { data = [], isLoading } = useV2List("transfer_bookings_v2", true);
  const del = useV2Delete("transfer_bookings_v2");
  const [viewBooking, setViewBooking] = useState<any>(null);
  const [deleteBooking, setDeleteBooking] = useState<any>(null);

  if (isLoading) return <p>Loading...</p>;
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Bookings</h2>
      <div className="rounded-lg border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-3 text-left">#</th>
              <th className="p-3 text-left">Customer</th>
              <th className="p-3 text-left">Route</th>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Total</th>
              <th className="p-3 text-left">Payment</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No bookings yet</td></tr>
            ) : data.map((b: any) => (
              <tr key={b.id} className="border-t">
                <td className="p-3 font-mono text-xs">{b.booking_number}</td>
                <td className="p-3">{b.customer_name}<br /><span className="text-xs text-muted-foreground">{b.customer_email}</span></td>
                <td className="p-3 text-xs">{b.origin?.name} → {b.destination?.name}</td>
                <td className="p-3">{b.pickup_date} {b.pickup_time}</td>
                <td className="p-3 font-semibold">${b.total}</td>
                <td className="p-3 text-xs">{b.payment_method || "—"}<br /><span className="text-muted-foreground">{b.payment_status}</span></td>
                <td className="p-3"><Badge>{b.status}</Badge></td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => setViewBooking(b)} title="View details">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleteBooking(b)} title="Delete">
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewBooking} onOpenChange={(o) => !o && setViewBooking(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Booking {viewBooking?.booking_number}</DialogTitle>
          </DialogHeader>
          {viewBooking && (
            <div className="space-y-3 text-sm">
              <Section title="Customer">
                <Row label="Name" value={viewBooking.customer_name} />
                <Row label="Email" value={viewBooking.customer_email} />
                <Row label="Phone" value={viewBooking.customer_phone} />
                <Row label="Flight #" value={viewBooking.flight_number} />
              </Section>
              <Section title="Trip">
                <Row label="From" value={viewBooking.origin?.name} />
                <Row label="To" value={viewBooking.destination?.name} />
                <Row label="Type" value={viewBooking.trip_type} />
                <Row label="Pickup" value={`${viewBooking.pickup_date} ${viewBooking.pickup_time}`} />
                {viewBooking.return_date && <Row label="Return" value={`${viewBooking.return_date} ${viewBooking.return_time}`} />}
                <Row label="Passengers" value={viewBooking.passengers} />
                <Row label="Luggage" value={viewBooking.luggage} />
                <Row label="Distance" value={`${viewBooking.distance_km} km`} />
              </Section>
              <Section title="Payment">
                <Row label="Method" value={viewBooking.payment_method || "—"} />
                <Row label="Status" value={viewBooking.payment_status} />
                <Row label="Reference" value={viewBooking.payment_reference || "—"} />
                <Row label="Subtotal" value={`$${viewBooking.subtotal}`} />
                <Row label="Discount" value={`$${viewBooking.discount}`} />
                <Row label="Total" value={`$${viewBooking.total}`} />
              </Section>
              {viewBooking.special_requests && (
                <Section title="Special Requests">
                  <p className="text-muted-foreground">{viewBooking.special_requests}</p>
                </Section>
              )}
              <Section title="Meta">
                <Row label="Status" value={viewBooking.status} />
                <Row label="Created" value={new Date(viewBooking.created_at).toLocaleString()} />
              </Section>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteBooking} onOpenChange={(o) => !o && setDeleteBooking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete booking?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete booking <span className="font-mono">{deleteBooking?.booking_number}</span>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteBooking) del.mutate(deleteBooking.id);
                setDeleteBooking(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border rounded-lg p-3">
      <h4 className="font-semibold mb-2 text-primary">{title}</h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value ?? "—"}</span>
    </div>
  );
}

